import type { ReactElement, ReactNode } from "react";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Icons, ic } from "../wizard-icons";

const K = keys.quickSetup;

/**
 * One-question-per-screen presentational shell: a small navy ✦ badge, the
 * step's heading (h2), a one-line subhead, then the control slot. Centered
 * ~640px column on desktop, full-width on mobile. Optional "‹ Back" link
 * (top-start, arrow flips with `dir`).
 */
export function QuestionScreen({
  heading,
  sub,
  onBack,
  showBack,
  narrow,
  loading,
  loadingSub,
  children,
}: {
  heading: string;
  sub: string;
  onBack?: () => void;
  showBack?: boolean;
  narrow: boolean;
  /** When true, the badge ✦ spins in place and the subhead becomes the
   *  wave-dot status line (`loadingSub`) — per the category-generating handoff. */
  loading?: boolean;
  loadingSub?: string;
  children: ReactNode;
}): ReactElement {
  const t = useTranslations();
  const dir = useLocale() === "ar" ? "rtl" : "ltr";
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {!narrow && showBack && onBack && (
        <button
          onClick={onBack}
          style={{
            position: "absolute",
            top: 28,
            insetInlineStart: 36,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: "transparent",
            border: "none",
            color: "var(--gold-ink)",
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "inherit",
            padding: 0,
          }}
        >
          <span style={{ display: "flex", transform: dir === "rtl" ? "scaleX(-1)" : undefined }}>
            {ic(Icons.back, 14)}
          </span>
          {t(K.header.back)}
        </button>
      )}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: narrow ? "24px 20px" : "40px 36px",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 640,
            display: "flex",
            flexDirection: "column",
            gap: narrow ? 20 : 28,
          }}
        >
          <div style={{ display: "flex", gap: narrow ? 12 : 14, alignItems: "flex-start" }}>
            <div
              style={{
                width: narrow ? 32 : 36,
                height: narrow ? 32 : 36,
                borderRadius: narrow ? 9 : 10,
                background: "var(--navy)",
                color: "var(--accent)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: narrow ? 14 : 16,
                flexShrink: 0,
              }}
            >
              {/* Filled ✦ (per design). When generating, this glyph — inside
                  the navy tile — is what spins (handoff: "the ✦ glyph inside
                  the navy tile spins"), not a separate element. */}
              <span
                className={loading ? "qs-cs-sparkle-spin" : undefined}
                style={{ display: "flex" }}
                aria-hidden="true"
              >
                {/* Inline (not `ic`) so we can shift the viewBox: the sparkle
                    path's geometric center is (12, 9), not (12, 12), so a plain
                    0 0 24 24 box renders it high and would make it orbit when
                    spinning. `viewBox="0 -3 24 24"` centers the star in the box
                    (and puts the rotation origin at the star's true center).
                    Sized to 24/21px to match the handoff's 16px ✦ glyph. */}
                <svg
                  width={narrow ? 21 : 24}
                  height={narrow ? 21 : 24}
                  viewBox="0 -3 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2l1.8 5.5L19 9l-5.2 1.5L12 16l-1.8-5.5L5 9l5.2-1.5L12 2z" />
                </svg>
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: narrow ? 22 : 30,
                  fontWeight: 800,
                  letterSpacing: -0.4,
                  lineHeight: 1.25,
                  color: "var(--navy)",
                  textAlign: "start",
                }}
              >
                {heading}
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: narrow ? 13 : 15,
                  color: "var(--muted)",
                  textAlign: "start",
                }}
              >
                {loading && loadingSub ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                    {loadingSub}
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                      <span className="qs-cs-dot" />
                      <span className="qs-cs-dot" />
                      <span className="qs-cs-dot" />
                    </span>
                  </span>
                ) : (
                  sub
                )}
              </p>
            </div>
          </div>

          <div>{children}</div>
        </div>
      </div>
    </div>
  );
}
